import { ReactNode } from 'react';
import { Navigate } from '@tanstack/react-router';
import { FeatureFlagName } from '../config/feature-flags';
import useFeatureFlag from '../hooks/useFeatureFlag';

interface FeatureFlagRouteProps {
  /**
   * The feature flag name to check
   */
  flag: FeatureFlagName;

  /**
   * The component to render if the feature flag is enabled
   */
  children: ReactNode;

  /**
   * The path to redirect to if the feature flag is disabled
   * @default "/"
   */
  fallbackPath?: string;
}

/**
 * A component that conditionally renders its children based on a feature flag
 * If the feature flag is disabled, it redirects to the fallback path
 */
export function FeatureFlagRoute({
  flag,
  children,
  fallbackPath = '/',
}: FeatureFlagRouteProps) {
  const { enabled, loading } = useFeatureFlag(flag);

  // While loading, don't render anything to prevent flashing
  if (loading) {
    return null;
  }

  // If the feature flag is disabled, redirect to the fallback path
  if (!enabled) {
    return <Navigate to={fallbackPath} replace />;
  }

  // If the feature flag is enabled, render the children
  return <>{children}</>;
}

export default FeatureFlagRoute;
